import { useLocalization } from "@fluent/react";
import {
  useBreakpoints,
  Table,
  Button,
  Dialog,
  Chip,
  LoadingSkeleton,
} from "@narmi/design_system";
import { modules, cents } from "byzantine";
import Pagination from "byzantine/src/Pagination";
import filters from "byzantine/src/filters";
import { BulkPaymentRowType } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import PageNavigation from "../../page_navigation/PageNavigation";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { DIALOG_PAGE_SIZE, getDialogWidth } from "../common";
import SelectedRecipientCard from "./table/SelectedRecipientCard";
import styles from "./table/BulkPaymentTable.module.scss";
import { SPEED_COPY_CONFIG } from "./table/BulkPaymentRow";
import useShowCollectAmounts from "./useShowCollectAmounts";

const ReviewPaymentDialog = ({
  isOpen,
  handleClose,
  payments,
  pageNumber,
  setPageNumber,
  paymentsShape = "bulkRow",
  serverTotal,
  isLoading = false,
}: {
  isOpen: boolean;
  handleClose: () => void;
  // shape of `payments`: "bulkRow" (create-bulk review) reads transactionType /
  // speedStatus; "paymentActivity" reads isCollectPayment / isInstantPayment etc.
  payments: BulkPaymentRowType[] | API.FormattedPaymentActivity[];
  pageNumber: number;
  setPageNumber: (page: number) => void;
  paymentsShape?: "bulkRow" | "paymentActivity";
  // set when paging through the API; `payments` is then only the current page
  serverTotal?: number;
  isLoading?: boolean;
}) => {
  const { l10n } = useLocalization();
  const { m, l } = useBreakpoints();
  const features = useUserFeatures();
  const { expedited_payments: expeditedPaymentsFeature } = features;
  const recipients = modules.achPayments.usePermissionedRecipients();
  const displaySpeed = expeditedPaymentsFeature;
  const isPaymentActivity = paymentsShape === "paymentActivity";
  // payment-activity payments may be a single server page, so only bulk rows
  // contribute their direction
  const showCollectAmounts = useShowCollectAmounts(
    !isPaymentActivity &&
      (payments as BulkPaymentRowType[]).some(
        (p) => p.transactionType === "collect"
      )
  );

  const pagination = new Pagination({
    total: serverTotal ?? payments.length,
    page: pageNumber,
    pageSize: DIALOG_PAGE_SIZE,
    navigatePage: (page: number) => {
      setPageNumber(page);
    },
  });

  const startIndex = (pagination.page - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const currentPayments =
    serverTotal === undefined ? payments.slice(startIndex, endIndex) : payments;

  const COL_VISIBILITY: React.ComponentProps<typeof Table>["colVisibility"] =
    displaySpeed
      ? [
          "*", // date
          "*", // recipient
          "*", // action
          "*", // speed
          "m", // description (only show on medium+ screens)
          "*", // amount
        ]
      : [
          "*", // date
          "*", // recipient
          "*", // action
          "m", // description (only show on medium+ screens)
          "*", // amount
        ];

  const COL_LAYOUT: React.ComponentProps<typeof Table>["colLayout"] =
    displaySpeed
      ? {
          s: "1fr 1fr min-content max-content 1fr", // [date] [recipient] [action] [speed] [amount]
          m: "1fr 2fr 1fr max-content 2fr 1fr", // [date] [recipient] [action] [speed] [description] [amount]
          l: "1fr 2fr 1fr 2fr 2fr 1fr", // [date] [recipient] [action] [speed] [description] [amount]
        }
      : {
          s: "1fr 1fr min-content 1fr", // [date] [recipient] [action] [amount]
          m: "1fr 2fr 1fr 2fr 1fr", // [date] [recipient] [action] [description] [amount]
          l: "1fr 2fr 1fr 2fr 1fr", // [date] [recipient] [action] [description] [amount]
        };

  const getSpeedStatus = ({
    payment,
  }: {
    payment: BulkPaymentRowType | API.FormattedPaymentActivity;
  }) => {
    // bulk-row shape (create-bulk review) carries transactionType + speed status
    if (!isPaymentActivity) {
      const { speedStatusWithExpediteOption, transactionType } =
        payment as BulkPaymentRowType;
      let speedCopy =
        transactionType === "send"
          ? SPEED_COPY_CONFIG.standard.sending
          : SPEED_COPY_CONFIG.standard.collecting;

      if (speedStatusWithExpediteOption) {
        speedCopy =
          transactionType === "send"
            ? SPEED_COPY_CONFIG[speedStatusWithExpediteOption].sending
            : SPEED_COPY_CONFIG[speedStatusWithExpediteOption].collecting;
      }

      return (
        <Table.Cell>
          <Chip
            kind="info"
            label={speedCopy}
            startIcon={
              speedStatusWithExpediteOption
                ? SPEED_COPY_CONFIG[speedStatusWithExpediteOption].icon
                : SPEED_COPY_CONFIG.standard.icon
            }
          />
        </Table.Cell>
      );
    }

    const { isSameDayPayment, isInstantPayment, isCollectPayment } =
      payment as API.FormattedPaymentActivity;

    const getSpeedConfig = () => {
      if (isInstantPayment) {
        return SPEED_COPY_CONFIG.instant;
      } else if (isSameDayPayment) {
        return SPEED_COPY_CONFIG.sameDay;
      }
      return SPEED_COPY_CONFIG.standard;
    };

    const speedConfig = getSpeedConfig();

    return (
      <Table.Cell>
        <Chip
          kind="info"
          label={
            isCollectPayment ? speedConfig.collecting : speedConfig.sending
          }
          startIcon={speedConfig.icon}
        />
      </Table.Cell>
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button onClick={handleClose} testId="close-review-payment-dialog">
            {l10n.getString("label-close")}
          </Button>
        </div>
      }
      onUserDismiss={handleClose}
      title={l10n.getString("review-bulk-header")}
      width={getDialogWidth({ m, l })}
    >
      <div className={styles.reviewTableScroll}>
        <Table colVisibility={COL_VISIBILITY} colLayout={COL_LAYOUT}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {l10n.getString("label-date")}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {showCollectAmounts
                  ? l10n.getString("label-to-from")
                  : l10n.getString("ach-payment-recipient-title")}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("label-action")}
              </Table.HeaderCell>
              {displaySpeed ? (
                <Table.HeaderCell>
                  {l10n.getString("speed-label")}
                </Table.HeaderCell>
              ) : null}
              <Table.HeaderCell>
                {l10n.getString("transaction-detail-description")}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("label-amount")}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {currentPayments.map(
              (
                payment: BulkPaymentRowType | API.FormattedPaymentActivity,
                index: number
              ) => {
                const { scheduledDate, recipient, description, amount } =
                  payment;

                let formattedDate = "";
                if (isPaymentActivity && scheduledDate) {
                  formattedDate = new Date(scheduledDate).toLocaleDateString(
                    "en-US"
                  );
                } else if (scheduledDate instanceof Date) {
                  formattedDate = scheduledDate.toLocaleDateString("en-US");
                } else if (scheduledDate) {
                  formattedDate = String(scheduledDate);
                }

                const getPaymentAmount = () => {
                  const formattedAmount = filters.currency(cents(amount));

                  if (showCollectAmounts) {
                    if (isPaymentActivity) {
                      return (payment as API.FormattedPaymentActivity)
                        .isCollectPayment
                        ? `+${formattedAmount}`
                        : `-${formattedAmount}`;
                    }
                    if (
                      (payment as BulkPaymentRowType)?.transactionType ===
                      "collect"
                    ) {
                      return `+${formattedAmount}`;
                    } else if (
                      (payment as BulkPaymentRowType)?.transactionType ===
                      "send"
                    ) {
                      return `-${formattedAmount}`;
                    }
                  }

                  return formattedAmount;
                };

                const getDirection = () => {
                  if (isPaymentActivity) {
                    return (payment as API.FormattedPaymentActivity)
                      .isCollectPayment
                      ? "collect"
                      : "send";
                  }
                  return (
                    (payment as BulkPaymentRowType)?.transactionType || "send"
                  );
                };

                return (
                  <Table.Row key={`table-row__${index}`}>
                    <Table.Cell>
                      {formattedDate ||
                        l10n.getString("pending-table-one-time-payment")}
                    </Table.Cell>
                    <Table.Cell>
                      {recipient ? (
                        <SelectedRecipientCard
                          recipientId={recipient.id}
                          rowIndex={index}
                          recipients={recipients}
                          recipient={recipient}
                          staticCard={true}
                        />
                      ) : null}
                    </Table.Cell>
                    <Table.Cell>
                      <span className={styles.capitalize}>
                        {getDirection()}
                      </span>
                    </Table.Cell>
                    {displaySpeed
                      ? getSpeedStatus({
                          payment,
                        })
                      : null}
                    <Table.Cell>{description}</Table.Cell>
                    <Table.Cell>{getPaymentAmount()}</Table.Cell>
                  </Table.Row>
                );
              }
            )}
          </Table.Body>
        </Table>
      </div>
      <LoadingSkeleton
        isLoading={isLoading}
        content="paragraph"
        lines={DIALOG_PAGE_SIZE}
      />
      <PageNavigation pagination={pagination} marginClasses="margin--all--l" />
    </Dialog>
  );
};

export default ReviewPaymentDialog;
